import TableSortingIcon from "@/components/common/TableSortingIcon";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { ProductVariantsPopover } from "@/components/products/ProductVariantsPopover";
import { useConfirmation } from "@/components/common/ConfirmationDialog";
import useProducts from "@/hooks/useProducts";
import { Product, VariantApiShape } from "@/types/models/product";
import { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import {
  EllipsisVertical,
  Pencil,
  Package,
  Tag,
  Eye,
  Trash2,
  Copy,
} from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { formatCurrency } from "@/utils/currency";
import { toast } from "sonner";
import { useState } from "react";
import { generateImageSrc } from "@/utils/helper";

// Helper function to get variant display name from options
const getVariantDisplayName = (variant: any): string => {
  const options = [variant?.option1, variant?.option2, variant?.option3].filter(
    (opt) => opt && opt.trim() !== "",
  );
  if (options.length > 0) {
    return options.join(" / ");
  }
  return variant?.title || "Unknown";
};

const getStatusColor = (status: string) => {
  switch (status) {
    case "ACTIVE":
      return "bg-green-500/20 text-green-600 hover:bg-green-500/20";
    case "DRAFT":
      return "bg-orange-500/20 text-orange-600 hover:bg-orange-500/20";
    case "INACTIVE":
      return "bg-red-500/20 text-red-600 hover:bg-red-500/20";
    default:
      return "bg-gray-500/20 text-gray-600 hover:bg-gray-500/20";
  }
};

const formatStatus = (status: string) => {
  switch (status) {
    case "ACTIVE":
      return "Active";
    case "DRAFT":
      return "Draft";
    case "INACTIVE":
      return "Inactive";
    default:
      return status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  }
};

const ProductThumbnail: React.FC<{ imageId?: string; title: string }> = ({
  imageId,
  title,
}) => {
  const [hasError, setHasError] = useState(false);

  if (!imageId || hasError) {
    return (
      <div className="w-10 h-10 bg-muted rounded-md flex items-center justify-center">
        <Package className="h-4 w-4 text-muted-foreground" />
      </div>
    );
  }

  return (
    <img
      src={imageId}
      alt={title}
      className="w-10 h-10 object-cover"
      onError={() => setHasError(true)}
    />
  );
};

const resolveTableImageUrl = (rawValue?: string): string => {
  if (!rawValue) return "";
  const value = rawValue.trim();
  if (!value) return "";

  if (/^https?:\/\//i.test(value)) {
    return value;
  }

  const normalized = value.startsWith("/") ? value.slice(1) : value;
  if (normalized.startsWith("store/")) {
    return generateImageSrc(normalized, 60, 60);
  }

  // Ignore bare IDs (UUIDs) to avoid invalid CDN requests like /<uuid>.
  return "";
};

export const getColumns = (): ColumnDef<Product>[] => [
  {
    id: "select",
    size: 50,
    minSize: 40,
    maxSize: 60,
    header: ({ table }) => {
      const { rows } = table.getRowModel();
      const selectedRows = table.getSelectedRowModel().rows;
      const isAllSelected =
        rows.length > 0 && selectedRows.length === rows.length;
      const isSomeSelected = selectedRows.length > 0 && !isAllSelected;

      return (
        <div
          className="flex items-center justify-center transition-opacity duration-200"
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            checked={
              isAllSelected ? true : isSomeSelected ? "indeterminate" : false
            }
            onCheckedChange={(value) => {
              if (value) {
                table.toggleAllPageRowsSelected(true);
              } else {
                table.toggleAllPageRowsSelected(false);
              }
            }}
            aria-label="Select all"
            className="!bg-transparent border-foreground text-foreground data-[state=checked]:text-foreground"
          />
        </div>
      );
    },
    cell: ({ row }) => {
      const isSelected = row.getIsSelected();

      return (
        <div
          className={`flex items-center justify-center transition-opacity duration-200 ${
            isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-100"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            checked={isSelected}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
            className="!bg-transparent border-foreground text-foreground data-[state=checked]:text-foreground"
          />
        </div>
      );
    },
  },
  {
    accessorKey: "title",
    size: 250,
    minSize: 200,
    maxSize: 350,
    header: ({ column }) => {
      return (
        <Button
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          variant="ghost"
          className="w-full flex p-0 items-center justify-between hover:!bg-transparent hover:text-inherit group"
        >
          <span className="text-base font-bold">Product</span>
          <div
            className={`transition-opacity duration-200 ${column.getIsSorted() ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
          >
            <TableSortingIcon sortDirection={column.getIsSorted()} />
          </div>
        </Button>
      );
    },
    cell: ({ row }) => {
      const product = row.original;
      const date = product.updated_at
        ? new Date(product.updated_at)
        : new Date();
      const formattedDate = format(date, "MMM dd yyyy");
      const assetPath =
        (Array.isArray((product as any)?.assets)
          ? (product as any).assets.find((a: any) => a?.asset?.path)?.asset
              ?.path
          : undefined) || (product as any)?.image_asset?.path;
      const rawProductImage =
        assetPath ||
        (Array.isArray(product.images) && product.images.length > 0
          ? product.images[0]
          : undefined) ||
        (product as any).image ||
        (product as any).imageAssetId;
      const productImage = resolveTableImageUrl(rawProductImage);

      return (
        <div className="flex gap-3 items-center">
          <div className="w-10 h-10 overflow-hidden object-cover rounded-md flex-shrink-0">
            <ProductThumbnail imageId={productImage} title={product.title} />
          </div>
          <div className="min-w-0 flex-1">
            <h3
              className="text-sm font-semibold truncate max-w-[200px]"
              title={product.title}
            >
              {product.title}
            </h3>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs text-muted-foreground">
                {formattedDate}
              </span>
              <span className="text-xs text-muted-foreground">•</span>
              <span className="text-xs text-muted-foreground font-mono">
                {product.slug || "No slug"}
              </span>
            </div>
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "price_range",
    size: 120,
    minSize: 100,
    maxSize: 150,
    header: ({ column }) => {
      return (
        <div className="group">
          <Button
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
            variant="ghost"
            className="w-full flex p-0 items-center justify-between hover:!bg-transparent hover:text-inherit"
          >
            <span className="text-base font-bold">Price</span>
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <TableSortingIcon sortDirection={column.getIsSorted()} />
            </div>
          </Button>
        </div>
      );
    },
    cell: ({ row }) => {
      const variants = row.original.variants;
      if (!variants || variants.length === 0) {
        return (
          <div>
            <h3 className="text-xs font-semibold">No variants</h3>
          </div>
        );
      }

      const prices = variants
        .map((v) => {
          const vv = v as VariantApiShape | any;
          if (typeof vv.price === "number") return vv.price;
          if (typeof vv.listing_price === "number") return vv.listing_price;
          if (typeof vv.listingPrice === "number") return vv.listingPrice;
          return 0;
        })
        .filter((p) => typeof p === "number");
      const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
      const maxPrice = prices.length > 0 ? Math.max(...prices) : 0;

      return (
        <div>
          <h3 className="text-xs font-semibold">
            {minPrice === maxPrice
              ? formatCurrency(minPrice)
              : `${formatCurrency(minPrice)} - ${formatCurrency(maxPrice)}`}
          </h3>
          {variants.length > 1 && (
            <span className="text-xs text-gray-600">
              {variants.length} variants
            </span>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "variants",
    size: 200,
    minSize: 150,
    maxSize: 300,
    header: () => <span className="text-base font-bold">Variants</span>,
    cell: ({ row }) => {
      const variants = row.original.variants;

      if (!variants || variants.length === 0) {
        return (
          <div>
            <h3 className="text-xs font-semibold">No variants</h3>
            <span className="text-xs text-gray-600">0 options</span>
          </div>
        );
      }

      const totalInventory = variants.reduce(
        (sum, variant) => sum + (variant.inventory ?? 0),
        0,
      );
      const variantCount = variants.length;

      const variantTitles = variants
        .slice(0, 2)
        .map((variant) => getVariantDisplayName(variant))
        .join(", ");
      const hasMore = variants.length > 2;

      const triggerContent = (
        <div>
          <h3
            className="text-xs font-semibold truncate max-w-[150px]"
            title={variantTitles}
          >
            {variantTitles}
            {hasMore ? ` +${variants.length - 2} more` : ""}
          </h3>
          <span className="text-xs text-gray-600">
            {totalInventory} in stock • {variantCount} variant
            {variantCount !== 1 ? "s" : ""}
          </span>
        </div>
      );

      return (
        <ProductVariantsPopover variants={variants} trigger={triggerContent} />
      );
    },
  },
  {
    accessorKey: "tags",
    size: 150,
    minSize: 120,
    maxSize: 200,
    header: () => <span className="text-base font-bold">Tags</span>,
    cell: ({ row }) => {
      const tags = row.original.tags || [];

      if (tags.length === 0) {
        return (
          <div>
            <span className="text-xs text-gray-500">No tags</span>
          </div>
        );
      }

      const displayTags = tags.slice(0, 2);
      const hasMore = tags.length > 2;
      const allTagsText = tags.join(", ");

      return (
        <div className="flex flex-wrap gap-1 max-w-[140px]" title={allTagsText}>
          {displayTags.map((tag, index) => (
            <Badge
              key={index}
              variant="secondary"
              className="text-xs px-2 py-0.5 border-0 text-blue-600 hover:text-blue-700 rounded-full truncate"
              style={{ backgroundColor: "#3b82f633" }}
              title={tag}
            >
              {tag}
            </Badge>
          ))}
          {hasMore && (
            <Badge
              variant="secondary"
              className="text-xs px-2 py-0.5 border-0 text-blue-600 hover:text-blue-700 rounded-full"
              style={{ backgroundColor: "#3b82f633" }}
              title={`+${tags.length - 2} more tags: ${tags.slice(2).join(", ")}`}
            >
              +{tags.length - 2}
            </Badge>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "status",
    size: 120,
    minSize: 100,
    maxSize: 160,
    header: () => <span className="text-base font-bold">Status</span>,
    cell: ({ row }) => {
      const status = row.original.status;
      return (
        <Badge
          variant="secondary"
          className={`rounded-full px-[10px] py-[5px] ${getStatusColor(status)}`}
        >
          {formatStatus(status)}
        </Badge>
      );
    },
  },
  {
    accessorKey: "actions",
    size: 200,
    minSize: 150,
    maxSize: 250,
    header: () => <span className="text-base font-bold">Actions</span>,
    cell: ({ row }) => <ProductActionsCell product={row.original} />,
  },
];

// Named export
export const columns = getColumns;

// Default export
export default getColumns;

const ProductActionsCell: React.FC<{
  product: Product;
}> = ({ product }) => {
  const params = useParams();
  const navigate = useNavigate();
  const { confirm } = useConfirmation();
  const { updateItem, deleteItem } = useProducts();

  const handleStatusToggle = () => {
    if (!product.id) return;
    const newStatus = product.status === "ACTIVE" ? "DRAFT" : "ACTIVE";
    updateItem(product.id, { status: newStatus });
  };

  const handleDelete = async () => {
    if (!product.id) return;

    const result = await confirm({
      title: "Delete Product",
      message: `Are you sure you want to delete "${product.title}"? This action cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      type: "destructive",
    });

    if (result.confirmed) {
      deleteItem(product.id);
      toast.success("Product deleted successfully");
    }
  };

  if (!params) return null;

  return (
    <div
      className="flex items-center gap-3"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-600">
          {product.status === "ACTIVE" ? "Active" : "Inactive"}
        </span>
        <Switch
          checked={product.status === "ACTIVE"}
          onCheckedChange={handleStatusToggle}
        />
      </div>

      <div className="flex items-center gap-1">
        <Link
          to={`/${params.storeType}/${params.storeId}/product/create?q=${product.id}`}
        >
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            <Pencil className="h-4 w-4" />
          </Button>
        </Link>

        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
          <Eye className="h-4 w-4" />
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <EllipsisVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="bg-card dark:bg-card text-foreground"
            align="end"
          >
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem>
              <Tag className="h-4 w-4 mr-2" />
              Manage Tags
            </DropdownMenuItem>
            <DropdownMenuItem>
              <Package className="h-4 w-4 mr-2" />
              View Variants
            </DropdownMenuItem>
            <DropdownMenuItem
              onSelect={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(
                  `/${params.storeType}/${params.storeId}/product/create?q=${product.id}&duplicate=1`,
                );
              }}
            >
              <Copy className="h-4 w-4 mr-2" />
              Duplicate Product
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={handleDelete}
              className="text-red-600 focus:text-red-600"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Product
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};
